import { useState } from "react";
import { useCachedResource } from "../hooks/useCachedResource";
import { useTranslation } from "react-i18next";
import { Plus, Send } from "lucide-react";
import { toast } from "sonner";
import { createInvoice, issueInvoice, listInvoices } from "../api/accounting";
import { AcctShell } from "../features/acct/AcctShell";
import { useAcctLookups } from "../features/acct/useAcctLookups";
import { NeuButton, NeuCard, NeuInput, NeuSelect } from "../components/neu/Neu";

const TONE = { draft: "text-muted-foreground", sent: "text-primary", partial: "text-primary", paid: "text-success", overdue: "text-destructive" };

export default function AcctInvoicesPage() {
  const { t } = useTranslation();
  const { customers } = useAcctLookups();
  const { data, reload: load } = useCachedResource("acct-invoices", () => listInvoices({ status: "all" }));
  const invoices = data || [];
  const [form, setForm] = useState({ number: "", customer_id: "", qty: 1, price: "", tax_percent: 5, description: "" });
  const [creating, setCreating] = useState(false);

  const save = async () => {
    await createInvoice({
      date: new Date().toISOString().slice(0, 10),
      customer_id: form.customer_id || customers[0]?.id,
      number: form.number || null,
      lines: [{ description: form.description, qty: Number(form.qty), price: Number(form.price), tax_percent: Number(form.tax_percent) }],
    });
    toast.success(t("acct.invoiceCreated"));
    setCreating(false);
    load();
  };

  const issue = async (invoice) => {
    await issueInvoice(invoice.id);
    toast.success(t("acct.invoiceIssued"));
    load();
  };

  return (
    <AcctShell
      titleKey="invoices"
      actions={
        <NeuButton variant="primary" className="flex items-center gap-2" data-testid="invoice-add-btn" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> {t("acct.newInvoice")}
        </NeuButton>
      }
    >
      {creating ? (
        <NeuCard className="grid gap-3 p-5 sm:grid-cols-6" data-testid="invoice-form">
          <NeuSelect data-testid="invoice-customer" value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
            <option value="">{t("acct.customer")}</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </NeuSelect>
          <NeuInput data-testid="invoice-number" placeholder={t("acct.invoiceNumber")} value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} />
          <NeuInput data-testid="invoice-desc" placeholder={t("acct.description")} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <NeuInput data-testid="invoice-qty" type="number" placeholder={t("acct.qty")} value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} />
          <NeuInput data-testid="invoice-price" type="number" placeholder={t("acct.price")} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          <NeuButton variant="primary" data-testid="invoice-save" disabled={!form.price} onClick={save}>
            {t("app.save")}
          </NeuButton>
        </NeuCard>
      ) : null}

      <div className="grid gap-3" data-testid="invoice-list">
        {invoices.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="invoice-empty">{t("acct.noInvoices")}</p> : null}
        {invoices.map((inv) => (
          <NeuCard key={inv.id} className="flex flex-wrap items-center gap-3 p-4" data-testid={`invoice-row-${inv.id}`}>
            <span className="font-mono text-xs font-bold" dir="ltr">{inv.number || inv.id.slice(-6)}</span>
            <span className="font-mono text-xs" dir="ltr">{inv.date}</span>
            <span className="min-w-0 flex-1 truncate text-sm">{inv.lines?.[0]?.description || "—"}</span>
            <span className="font-mono text-sm font-bold" dir="ltr">{inv.total}</span>
            <span className={`text-xs font-bold ${TONE[inv.status]}`}>{t(`acct.invoiceStatus.${inv.status}`)}</span>
            {inv.status === "draft" ? (
              <NeuButton className="px-3 py-2 text-xs" data-testid={`invoice-issue-${inv.id}`} onClick={() => issue(inv)}>
                <Send className="h-3.5 w-3.5" /> {t("acct.issue")}
              </NeuButton>
            ) : (
              <span className="font-mono text-[11px] text-primary" dir="ltr">{inv.entry_number}</span>
            )}
          </NeuCard>
        ))}
      </div>
    </AcctShell>
  );
}
